/**
 * A player's country, read from `player.nationality`.
 *
 * `nationality` is free text typed on the profile form ("Australia",
 * "Malaysian", "台灣🇹🇼", "ROC", and junk). Public pages show a country only
 * when the text names one in this table; anything else returns null and the
 * caller shows something else (the division). Raw text is never displayed.
 *
 * Add a country by adding one line: its alpha-2, alpha-3, and the spellings
 * players use (lower case; CJK as written). Lookup ignores case, dots, extra
 * spaces and flag emoji; a lone flag emoji is read as its two letters.
 */
export type PlayerCountry = { code: string; iso3: string };

const COUNTRIES: ReadonlyArray<readonly [string, string, readonly string[]]> = [
  ['AU', 'AUS', ['australia', 'australian', '澳洲', '澳大利亞', '澳大利亚', 'オーストラリア']],
  ['NZ', 'NZL', ['new zealand', 'new zealander', '紐西蘭', '新西兰', 'ニュージーランド']],
  [
    'TW',
    'TWN',
    [
      'taiwan',
      'taiwanese',
      'roc',
      'republic of china',
      'chinese taipei',
      '台灣',
      '臺灣',
      '台湾',
      '中華民國',
      '中华民国',
      '中華台北',
    ],
  ],
  ['JP', 'JPN', ['japan', 'japanese', '日本']],
  ['MY', 'MYS', ['malaysia', 'malaysian', '馬來西亞', '马来西亚', 'マレーシア']],
  ['SG', 'SGP', ['singapore', 'singaporean', '新加坡', 'シンガポール']],
  ['HK', 'HKG', ['hong kong', 'hongkong', '香港']],
  ['MO', 'MAC', ['macau', 'macao', '澳門', '澳门']],
  ['CN', 'CHN', ['china', "people's republic of china", 'prc', '中國', '中国']],
  [
    'KR',
    'KOR',
    ['south korea', 'korea', 'korean', 'republic of korea', '韓國', '韩国', '韓国', '대한민국'],
  ],
  ['PH', 'PHL', ['philippines', 'filipino', '菲律賓']],
  ['ID', 'IDN', ['indonesia', 'indonesian', '印尼']],
  ['TH', 'THA', ['thailand', 'thai', '泰國']],
  ['VN', 'VNM', ['vietnam', 'viet nam', 'vietnamese', '越南']],
  ['IN', 'IND', ['india', 'indian', '印度']],
  ['PK', 'PAK', ['pakistan', 'pakistani']],
  ['EG', 'EGY', ['egypt', 'egyptian']],
  [
    'US',
    'USA',
    [
      'united states',
      'united states of america',
      'america',
      'american',
      '美國',
      '美国',
      'アメリカ',
    ],
  ],
  ['CA', 'CAN', ['canada', 'canadian', '加拿大']],
  [
    'GB',
    'GBR',
    [
      'united kingdom',
      'great britain',
      'britain',
      'british',
      'england',
      'english',
      'scotland',
      'scottish',
      'wales',
      'welsh',
      '英國',
      '英国',
      'イギリス',
    ],
  ],
  ['IE', 'IRL', ['ireland', 'irish']],
  ['FR', 'FRA', ['france', 'french', '法國']],
  ['DE', 'DEU', ['germany', 'german', 'deutschland', '德國']],
  ['IT', 'ITA', ['italy', 'italian', '義大利']],
  ['ES', 'ESP', ['spain', 'spanish']],
  ['NL', 'NLD', ['netherlands', 'dutch', 'holland']],
  ['ZA', 'ZAF', ['south africa', 'south african']],
  ['NG', 'NGA', ['nigeria', 'nigerian']],
];

const BY_KEY = new Map<string, PlayerCountry>();
const BY_CODE = new Map<string, PlayerCountry>();
for (const [code, iso3, names] of COUNTRIES) {
  const country = { code, iso3 };
  BY_CODE.set(code, country);
  for (const key of [code.toLowerCase(), iso3.toLowerCase(), ...names]) BY_KEY.set(key, country);
}

const REGIONAL_INDICATORS = /[\u{1F1E6}-\u{1F1FF}]/gu;
const REGIONAL_A = 0x1f1e6;

function normalise(text: string): string {
  return text
    .normalize('NFKC')
    .replace(REGIONAL_INDICATORS, '')
    .replace(/\./g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** "🇹🇼" → "TW"; null when the text holds no complete flag. */
function codeFromFlag(text: string): string | null {
  const letters = [...(text.match(REGIONAL_INDICATORS) ?? [])].map((ch) =>
    String.fromCharCode(65 + (ch.codePointAt(0)! - REGIONAL_A)),
  );
  return letters.length >= 2 ? letters[0] + letters[1] : null;
}

export function resolvePlayerCountry(nationality: string | null | undefined): PlayerCountry | null {
  if (!nationality) return null;
  const byText = BY_KEY.get(normalise(nationality));
  if (byText) return byText;
  const flagCode = codeFromFlag(nationality);
  return flagCode ? (BY_CODE.get(flagCode) ?? null) : null;
}

export function flagEmoji(code: string): string {
  return [...code.toUpperCase()]
    .map((c) => String.fromCodePoint(REGIONAL_A + c.charCodeAt(0) - 65))
    .join('');
}
